import React, { ReactNode, useState, useEffect } from 'react'
import { Layout, Menu, Avatar, Dropdown, Button, Typography, Badge, Tooltip, Drawer } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  DashboardOutlined,
  FileTextOutlined,
  DollarOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuOutlined,
  BellOutlined,
  SettingOutlined,
  WifiOutlined,
  DisconnectOutlined,
  LayoutOutlined,
  FileProtectOutlined,
  ShoppingOutlined,
  CloseOutlined,
  CreditCardOutlined
} from '@ant-design/icons'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { useWebSocket } from '../../hooks/useWebSocket'

const { Header, Content, Footer, Sider } = Layout
const { Text } = Typography

interface CustomerLayoutProps {
  children: ReactNode
}

export const CustomerLayout: React.FC<CustomerLayoutProps> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { portalConfig } = useCustomerAuth()
  const { customer, business, logout } = useCustomerAuth()
  const [collapsed, setCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [notifications, setNotifications] = useState<any[]>([])

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false)
      }
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // WebSocket integration for real-time updates
  const { isConnected, connectionError } = useWebSocket({
    onJobStatusUpdate: (update) => {
      console.log('📱 Job status updated:', update)
      // Add notification to list
      setNotifications(prev => [{
        id: Date.now(),
        type: 'job_update',
        title: 'Job Status Updated',
        message: `Job "${update.job.title}" is now ${update.status}`,
        timestamp: new Date().toISOString()
      }, ...prev.slice(0, 9)]) // Keep only last 10 notifications
    },
    onPaymentNotification: (notification) => {
      console.log('💳 Payment notification:', notification)
      // Add payment notification
      setNotifications(prev => [{
        id: Date.now(),
        type: 'payment',
        title: 'Payment Processed',
        message: notification.message,
        timestamp: new Date().toISOString()
      }, ...prev.slice(0, 9)])
    },
    onInvoiceNotification: (notification) => {
      console.log('📄 Invoice notification:', notification)
      // Add invoice notification
      setNotifications(prev => [{
        id: Date.now(),
        type: 'invoice',
        title: 'New Invoice',
        message: notification.message,
        timestamp: new Date().toISOString()
      }, ...prev.slice(0, 9)])
    }
  })

  // Menu items - filtered and labeled based on portal configuration
  const allMenuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      defaultLabel: 'Dashboard',
      configKey: 'dashboard' as const,
    },
    {
      key: '/jobs',
      icon: <FileTextOutlined />,
      defaultLabel: 'My Jobs',
      configKey: 'jobs' as const,
    },
    {
      key: '/estimates',
      icon: <FileProtectOutlined />,
      defaultLabel: 'Estimates',
      configKey: 'estimates' as const,
    },
    {
      key: '/invoices',
      icon: <DollarOutlined />,
      defaultLabel: 'Invoices',
      configKey: 'invoices' as const,
    },
    {
      key: '/products',
      icon: <ShoppingOutlined />,
      defaultLabel: 'Browse Products',
      configKey: 'products' as const,
    },
    {
      key: '/visualizer',
      icon: <LayoutOutlined />,
      defaultLabel: 'Project Visualizer',
      configKey: 'visualizer' as const,
    },
    {
      key: '/subscriptions',
      icon: <CreditCardOutlined />,
      defaultLabel: 'Subscriptions',
      configKey: 'subscriptions' as const,
    },
    {
      key: '/profile',
      icon: <UserOutlined />,
      defaultLabel: 'Profile',
      configKey: 'profile' as const,
    },
  ]

  // Filter menu items based on portal configuration
  const menuItems = allMenuItems
    .filter((item) => {
      // If no portal config exists, show all (default behavior)
      if (!portalConfig || !portalConfig.navigationLinks) {
        console.log('🔍 [CustomerLayout] No portal config, showing all links')
        return true
      }
      
      const linkConfig = portalConfig.navigationLinks[item.configKey]
      
      // If linkConfig doesn't exist, it means the backend didn't return it
      // This shouldn't happen if backend returns all links, but handle it safely
      if (!linkConfig) {
        console.warn(`🔍 [CustomerLayout] Link config missing for ${item.configKey}, defaulting to disabled`)
        return false
      }
      
      // Only show if explicitly enabled (true)
      const isEnabled = linkConfig.enabled === true
      console.log(`🔍 [CustomerLayout] Link ${item.configKey}: enabled=${linkConfig.enabled}, isEnabled=${isEnabled}, label="${linkConfig.label || item.defaultLabel}"`)
      return isEnabled
    })
    .map((item) => {
      const linkConfig = portalConfig?.navigationLinks?.[item.configKey]
      // Use custom label if provided, otherwise use default
      const label = linkConfig?.label || item.defaultLabel
      
      return {
        key: item.key,
        icon: item.icon,
        label: label,
      }
    })
  
  console.log('🔍 [CustomerLayout] Portal config:', portalConfig)
  console.log('🔍 [CustomerLayout] Filtered menu items:', menuItems.map(m => ({ key: m.key, label: m.label })))

  // User dropdown menu
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'My Profile',
      onClick: () => {
        navigate('/profile')
        setMobileMenuOpen(false)
      },
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => {
        navigate('/profile')
        setMobileMenuOpen(false)
      },
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign Out',
      onClick: logout,
    },
  ]

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key)
    if (isMobile) {
      setMobileMenuOpen(false)
    }
  }

  const mobileMenuContent = (
    <div>
      <div style={{ 
        padding: '16px', 
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <div style={{ 
            fontSize: '18px', 
            fontWeight: 'bold', 
            color: '#1890ff',
            marginBottom: '4px'
          }}>
            {business?.name || 'ServiceFlow Pro'}
          </div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Customer Portal
          </Text>
        </div>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={() => setMobileMenuOpen(false)}
        />
      </div>
      <Menu
        theme="light"
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        onClick={handleMenuClick}
        style={{ borderRight: 0 }}
      />
    </div>
  )

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider 
          collapsible 
          collapsed={collapsed} 
          onCollapse={setCollapsed}
          theme="light"
          breakpoint="lg"
          collapsedWidth={isMobile ? 0 : 80}
          style={{
            boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
            zIndex: 10,
          }}
        >
          <div style={{ 
            padding: '16px', 
            textAlign: 'center',
            borderBottom: '1px solid #f0f0f0'
          }}>
            {!collapsed ? (
              <>
                <div style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold', 
                  color: '#1890ff',
                  marginBottom: '4px'
                }}>
                  {business?.name || 'ServiceFlow Pro'}
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Customer Portal
                </Text>
              </>
            ) : (
              <div style={{ 
                width: '32px', 
                height: '32px', 
                background: '#1890ff', 
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                margin: '0 auto'
              }}>
                {business?.name?.charAt(0) || 'S'}
              </div>
            )}
          </div>

          <Menu
            theme="light"
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={handleMenuClick}
            style={{ borderRight: 0 }}
          />
        </Sider>
      )}

      {/* Mobile Drawer */}
      <Drawer
        title={null}
        placement="left"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        styles={{ body: { padding: 0 } }}
        width={280}
      >
        {mobileMenuContent}
      </Drawer>

      <Layout>
        <Header style={{ 
          background: '#fff', 
          padding: isMobile ? '0 16px' : '0 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            {isMobile ? (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setMobileMenuOpen(true)}
                style={{ marginRight: '12px' }}
              />
            ) : (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{ marginRight: '16px' }}
              />
            )}
            <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>
              {isMobile ? customer?.firstName : `Welcome back, ${customer?.firstName}!`}
            </Text>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '16px' }}>
            {/* Connection Status Indicator */}
            <Tooltip title={isConnected ? 'Connected to real-time updates' : connectionError || 'Disconnected'}>
              <Button
                type="text"
                icon={isConnected ? <WifiOutlined style={{ color: '#52c41a' }} /> : <DisconnectOutlined style={{ color: '#ff4d4f' }} />}
                size="small"
              />
            </Tooltip>

            {/* Notifications */}
            <Dropdown
              menu={{
                items: notifications.length > 0 ? [
                  ...notifications.map(notif => ({
                    key: notif.id,
                    label: (
                      <div style={{ maxWidth: isMobile ? '200px' : '250px', padding: '8px 0' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '14px' }}>
                          {notif.title}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {notif.message}
                        </div>
                        <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                          {new Date(notif.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    ),
                    disabled: true
                  })),
                  {
                    type: 'divider' as const
                  },
                  {
                    key: 'clear',
                    label: 'Clear all notifications',
                    onClick: () => setNotifications([])
                  }
                ] : [
                  {
                    key: 'empty',
                    label: 'No new notifications',
                    disabled: true
                  }
                ]
              }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Badge count={notifications.length} size="small">
                <Button
                  type="text"
                  icon={<BellOutlined />}
                  style={{ color: '#666' }}
                />
              </Badge>
            </Dropdown>
            
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '6px',
                transition: 'background-color 0.3s'
              }}>
                <Avatar 
                  size="small" 
                  icon={<UserOutlined />}
                  style={{ marginRight: isMobile ? '4px' : '8px' }}
                />
                {!isMobile && (
                  <Text style={{ color: '#666', fontSize: '14px' }}>
                    {customer?.firstName} {customer?.lastName}
                  </Text>
                )}
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content style={{ 
          margin: isMobile ? '16px' : '24px',
          padding: isMobile ? '0' : '0',
          background: '#f5f5f5',
          minHeight: 'calc(100vh - 112px)'
        }}>
          <div style={{ 
            padding: isMobile ? '16px' : '24px',
            background: '#fff',
            borderRadius: '8px',
            minHeight: 'calc(100vh - 160px)'
          }}>
            {children}
          </div>
        </Content>

        <Footer style={{ 
          textAlign: 'center',
          background: '#fff',
          borderTop: '1px solid #f0f0f0',
          padding: isMobile ? '12px 16px' : '16px 24px'
        }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            © {new Date().getFullYear()} {business?.name || 'ServiceFlow Pro'}. All rights reserved.
          </Text>
          {!isMobile && (
            <>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Powered by ServiceFlow Pro Customer Portal
              </Text>
            </>
          )}
        </Footer>
      </Layout>
    </Layout>
  )
}
